// Pure logic for ConfirmDialog — the reusable destructive-confirm shell.
//
// The component (ConfirmDialog.tsx) is thin React glue around these
// functions. Per the repo's test idiom (see type-to-confirm-helpers +
// its test), the gating + state-resolution + error-classification logic
// lives here so it can be unit-tested without a DOM, and the rendering
// is trusted through manual QA + the live archive consumer.
//
// Two states, decided by permission UP FRONT:
//   * "confirm" — the explanation, optional reason field, Cancel + the
//                 destructive button.
//   * "denied"  — a blocked state (error banner + plain reason + what to
//                 do), with only a Close button. We never render a form
//                 the user cannot submit.
//
// We also classify a 403 returned ON SUBMIT (a race — permission changed
// between open and click) so the open dialog can switch to an inline
// error instead of failing silently.

/** Which of the two states the dialog renders. Decided from the real
 *  permission BEFORE render, never after the user tries. */
export type ConfirmDialogMode = "confirm" | "denied";

export function resolveConfirmMode(allowed: boolean): ConfirmDialogMode {
  return allowed ? "confirm" : "denied";
}

/**
 * Whether the destructive button may be clicked. False whenever the
 * user isn't allowed, the action is already running, or a required
 * reason hasn't been entered (whitespace-only doesn't count). This is
 * the single source of truth for the disabled state — the reason field
 * gates the destructive button.
 */
export function canSubmitConfirm(opts: {
  allowed: boolean;
  running: boolean;
  reasonRequired: boolean;
  reason: string;
}): boolean {
  if (!opts.allowed) return false;
  if (opts.running) return false;
  if (opts.reasonRequired && opts.reason.trim().length === 0) return false;
  return true;
}

/** True for an HTTP 403 (forbidden). Used to detect a submit-time
 *  permission race so we can surface it inline. */
export function isForbiddenStatus(status: number | null | undefined): boolean {
  return status === 403;
}

/**
 * Turn a thrown value from the action into a plain-English message for
 * the inline error banner. Recognizes the app's ApiError shape (a
 * `status: number` plus `message`) without importing it, so this stays
 * dependency-free and node-testable. A 403 gets a permission-specific
 * line (the race case); anything else echoes the message.
 */
export function describeConfirmError(
  err: unknown,
  opts: { forbiddenMessage?: string } = {},
): string {
  const forbidden =
    opts.forbiddenMessage ??
    "You don't have permission to take this action. It may have changed since you opened this dialog.";

  if (err && typeof err === "object" && "status" in err) {
    const status = (err as { status?: unknown }).status;
    if (typeof status === "number") {
      if (isForbiddenStatus(status)) return forbidden;
      // The status number stays out of the sentence: it is a developer
      // detail (it lives in the network log), not something to read.
      if (
        "message" in err &&
        typeof (err as { message?: unknown }).message === "string" &&
        (err as { message: string }).message.trim() !== ""
      ) {
        return (err as { message: string }).message;
      }
      return "Something went wrong. Please try again.";
    }
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

// ─── Archive-household consumer copy ────────────────────────────────────
//
// The first consumer of ConfirmDialog. These builders keep the
// human-language strings out of the page component (and testable). They
// pull from REAL data — the attached-contact count flows in from the
// loaded household.

/** Pluralize "1 attached contact stays" vs "N attached contacts stay". */
export function attachedContactsLine(count: number): string {
  if (count === 1) {
    return "The 1 attached contact stays attached, but is hidden from default views unless you open it directly.";
  }
  return `The ${count} attached contact${count === 1 ? "" : "s"} stay attached, but are hidden from default views unless you open them directly.`;
}

/**
 * The "what archiving does" bullets for a household, in plain language,
 * built from real data. Mirrors the approved reference dialog.
 */
export function buildArchiveHouseholdConsequences(opts: {
  attachedContactCount: number;
}): string[] {
  return [
    "The household stays in the database. It is soft archived with a date and reason, not deleted.",
    attachedContactsLine(opts.attachedContactCount),
    "Service records, opportunities, transactions, and rhythms are kept, but left out of default analytics.",
    "Cross-company dashboards, like the Financial firm dashboard and capacity planning, will exclude this household.",
  ];
}
